import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { reais } from '../../premiacao';
import { mostraValor, useValoresOcultos } from '../../design/sigilo';
import { oQueEstaPagando } from '../../vendas';
import type { CardsPorBase, OQueEstaPagando, ParametrosPorBase } from '../../vendas';
import './pagando.css';

/**
 * O QUE ESTÁ PAGANDO — a vitrine do balconista (dono, 25/09/2026). Tudo que paga prêmio agora,
 * separado por programa: por quanto vender, quanto ganha e quanto tem. Sem custo e sem lote —
 * o servidor nem manda. Os números são os mesmos da tela de venda.
 */

type Aba = 'vitamina' | 'preVencido' | 'paradao';
const ABAS: readonly { a: Aba; rotulo: string }[] = [
  { a: 'vitamina', rotulo: 'Vitamina' },
  { a: 'preVencido', rotulo: 'Pré-vencido' },
  { a: 'paradao', rotulo: 'Paradão' },
];

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const mesAno = (am: string | null) => (am ? `${am.slice(5, 7)}/${am.slice(0, 4)}` : null);
const unidades = (q: string | null) =>
  q === null
    ? 'quantidade não informada'
    : `${q.replace('.', ',')} ${q === '1' ? 'unidade' : 'unidades'}`;

export function Pagando() {
  const ocultos = useValoresOcultos();
  const [d, setD] = useState<OQueEstaPagando | null>(null);
  const [erro, setErro] = useState(false);
  const [aba, setAba] = useState<Aba>('vitamina');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(() => {
    setErro(false);
    void oQueEstaPagando().then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, []);
  useEffect(carregar, [carregar]);

  const termo = semAcento(busca.trim());
  const passa = useCallback(
    (i: { descricao: string; codigo: string | null; fabricante: string | null }) =>
      !termo ||
      semAcento(i.descricao).includes(termo) ||
      (i.codigo ?? '').includes(termo) ||
      semAcento(i.fabricante ?? '').includes(termo),
    [termo],
  );

  const contagem = useMemo(
    () => ({
      vitamina: d?.vitamina.liberado ? d.vitamina.itens.length : null,
      preVencido: d?.preVencido.liberado ? d.preVencido.itens.length : null,
      paradao: d?.paradao.liberado ? d.paradao.itens.length : null,
    }),
    [d],
  );

  return (
    <div className="pg">
      <div className="pg-topo">
        <h1>O que está pagando</h1>
        <p>
          {ocultos
            ? 'Por quanto vender e quanto tem na loja'
            : 'Por quanto vender, quanto você ganha e quanto tem na loja'}{' '}
          · <a href="#/como-funciona">como funciona</a>
        </p>
      </div>

      <div className="pg-abas" role="tablist" aria-label="Programa">
        {ABAS.map((x) => (
          <button
            key={x.a}
            type="button"
            role="tab"
            aria-selected={aba === x.a}
            className={aba === x.a ? 'pg-sel' : ''}
            onClick={() => setAba(x.a)}
          >
            {x.rotulo}
            {contagem[x.a] !== null && <span className="pg-n">{contagem[x.a]}</span>}
          </button>
        ))}
      </div>

      <input
        className="pg-busca"
        type="search"
        placeholder="Buscar por nome, código ou fabricante"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />

      {erro ? (
        <ErroDeArea oQue="o que está pagando" aoTentar={carregar} />
      ) : !d ? (
        <div className="pg-carregando" aria-busy="true" />
      ) : aba === 'vitamina' ? (
        d.vitamina.liberado ? (
          <Lista vazio="Nenhuma vitamina pagando agora.">
            {d.vitamina.itens.filter(passa).map((i) => (
              <Item
                key={i.productId}
                id={i.productId}
                nome={i.descricao}
                codigo={i.codigo}
                fab={i.fabricante}
              >
                <div className="pg-cards">
                  {i.cards.map((c, k) => (
                    <div key={k} className={`pg-card${k === 0 ? ' pg-card-1' : ''}`}>
                      <span className="pg-card-rotulo">
                        Card {k === 0 ? 'A' : 'B'}
                        {i.cards.length > 1 ? (k === 0 ? ' · máx' : ' · mín') : ''}
                      </span>
                      <span>
                        a partir de <b>{reais(Number(c.aPartirDe))}</b>
                      </span>
                      {mostraValor(ocultos, c.premio) && (
                        <span className="pg-ganha">ganha {reais(Number(c.premio))}</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="pg-rodape">Estoque: {unidades(i.estoque)}</div>
              </Item>
            ))}
          </Lista>
        ) : (
          <NaoLiberado programa="A vitamina" />
        )
      ) : aba === 'preVencido' ? (
        d.preVencido.liberado ? (
          <Lista vazio="Nenhum pré-vencido pagando agora.">
            {d.preVencido.itens.filter(passa).map((i) => (
              <Item
                key={i.productId}
                id={i.productId}
                nome={i.descricao}
                codigo={i.codigo}
                fab={i.fabricante}
              >
                <DoisCards c={i} regra={d.preVencido.liberado ? d.preVencido.regra : null} />
                <div className="pg-rodape">
                  {unidades(i.quantidade)}
                  {i.venceEm && ` · vence em ${mesAno(i.venceEm) ?? ''}`}
                </div>
              </Item>
            ))}
          </Lista>
        ) : (
          <NaoLiberado programa="O pré-vencido" />
        )
      ) : d.paradao.liberado ? (
        <Lista vazio="Nenhum item no paradão agora.">
          {d.paradao.itens.filter(passa).map((i) => (
            <Item
              key={i.productId}
              id={i.productId}
              nome={i.descricao}
              codigo={i.codigo}
              fab={i.fabricante}
            >
              <DoisCards c={i} regra={d.paradao.liberado ? d.paradao.regra : null} />
              <div className="pg-rodape">Estoque: {unidades(i.estoque)}</div>
            </Item>
          ))}
        </Lista>
      ) : (
        <NaoLiberado programa="O paradão" />
      )}
    </div>
  );
}

function Lista({ vazio, children }: { vazio: string; children: React.ReactNode[] }) {
  if (children.length === 0) return <p className="pg-vazio">{vazio}</p>;
  return <ul className="pg-lista">{children}</ul>;
}

function Item({
  id,
  nome,
  codigo,
  fab,
  children,
}: {
  id: string;
  nome: string;
  codigo: string | null;
  fab: string | null;
  children: React.ReactNode;
}) {
  return (
    <li className="pg-item">
      <div className="pg-item-topo">
        <div>
          <div className="pg-nome">{nome}</div>
          <div className="pg-sub">
            {codigo ? `cód ${codigo}` : 'sem código'}
            {fab ? ` · ${fab}` : ''}
          </div>
        </div>
        <a className="pg-vender" href={`#/vendas?produto=${encodeURIComponent(id)}`}>
          Vender
        </a>
      </div>
      {children}
    </li>
  );
}

/**
 * Pré-vencido e paradão, como no 1.0: card A no preço base (ganha o prêmio base) e card B no PMC
 * (ganha o maior entre o prêmio base e X% do que passar da base, até o teto). Os valores vêm
 * prontos do servidor, pelo mesmo motor da venda.
 */
function DoisCards({ c, regra }: { c: CardsPorBase; regra: ParametrosPorBase | null }) {
  const ocultos = useValoresOcultos();
  if (!c.venderPor)
    return <div className="pg-sem-base">Sem preço base cadastrado: não paga premiação.</div>;
  return (
    <>
      <div className="pg-cards">
        <div className="pg-card">
          <span className="pg-card-rotulo">Card A · preço base</span>
          <span>
            vender por <b>{reais(Number(c.venderPor))}</b>
          </span>
          {mostraValor(ocultos, c.premio) && (
            <span className="pg-ganha">ganha {reais(Number(c.premio))}</span>
          )}
        </div>
        {c.pmc && (c.premioNoPmc || ocultos) ? (
          <div className="pg-card pg-card-1">
            <span className="pg-card-rotulo">Card B · PMC</span>
            <span>
              vender por <b>{reais(Number(c.pmc))}</b>
            </span>
            {mostraValor(ocultos, c.premioNoPmc) && (
              <span className="pg-ganha">ganha {reais(Number(c.premioNoPmc))}</span>
            )}
          </div>
        ) : (
          <div className="pg-card pg-card-vazio">
            <span className="pg-card-rotulo">Card B · PMC</span>
            <span>sem PMC cadastrado</span>
          </div>
        )}
      </div>
      {regra && !ocultos && mostraValor(ocultos, regra.premioBase) && (
        <div className="pg-regra">
          Vendeu acima de {reais(Number(c.venderPor))}? Ganha{' '}
          <b>{regra.pctDoAumento}% do que passar</b> (mínimo {reais(Number(regra.premioBase))},
          máximo {reais(Number(regra.teto))}){c.pmc ? `, até o PMC de ${reais(Number(c.pmc))}` : ''}
          .
        </div>
      )}
    </>
  );
}

function NaoLiberado({ programa }: { programa: string }) {
  return <p className="pg-vazio">{programa} não está liberado para venda agora.</p>;
}
